import React, {useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {get, ref} from 'firebase/database';
import {database} from '../../services/firebase';
import {useAuth} from '../../contexts/AuthContext';
import {ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip} from 'chart.js';
import {Bar, Pie} from 'react-chartjs-2';
import Header from "../../components/common/Header";

// Enregistrer les composants Chart.js
ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function QuizResults() {
    const {quizId, sessionId} = useParams();
    const {currentUser} = useAuth();
    const navigate = useNavigate();
    const isTrainingMode = !quizId;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [results, setResults] = useState(null);
    const [quiz, setQuiz] = useState(null);
    const [questions, setQuestions] = useState([]);

    useEffect(() => {
        async function fetchResults() {
            try {
                if (!currentUser) {
                    setError('Vous devez être connecté pour voir les résultats.');
                    setLoading(false);
                    return;
                }

                let resultsRef;
                if (isTrainingMode) {
                    // Récupérer les résultats d'entraînement
                    resultsRef = ref(database, `gameResults/training/${currentUser.uid}/${sessionId}`);
                } else {
                    // Récupérer les résultats de quiz
                    resultsRef = ref(database, `gameResults/${quizId}/${sessionId}`);
                }

                const resultsSnapshot = await get(resultsRef);

                if (!resultsSnapshot.exists()) {
                    setError('Résultats introuvables.');
                    setLoading(false);
                    return;
                }

                const resultsData = resultsSnapshot.val();

                if (isTrainingMode) {
                    // Format des résultats d'entraînement
                    setResults({
                        startedAt: resultsData.startedAt,
                        endedAt: resultsData.endedAt,
                        config: resultsData.config,
                        playerData: resultsData.playerData,
                        results: resultsData.results,
                        responses: resultsData.responses || {}
                    });

                    // Récupérer les détails des questions
                    const questionIds = Object.values(resultsData.responses || {}).map(r => r.questionId);
                    const questionsData = [];

                    for (const questionId of questionIds) {
                        const questionRef = ref(database, `questionBank/${questionId}`);
                        const questionSnapshot = await get(questionRef);

                        if (questionSnapshot.exists()) {
                            questionsData.push({
                                id: questionId,
                                ...questionSnapshot.val()
                            });
                        }
                    }

                    setQuestions(questionsData);

                } else {
                    // Format des résultats de quiz normal
                    setResults({
                        startedAt: resultsData.startedAt,
                        endedAt: resultsData.endedAt,
                        quizData: resultsData.quizData,
                        playerData: resultsData.players?.[currentUser.uid] || {},
                        responses: resultsData.individualResponses?.[currentUser.uid] || {}
                    });

                    // Récupérer les détails du quiz
                    const quizRef = ref(database, `quizzes/${quizId}`);
                    const quizSnapshot = await get(quizRef);

                    if (quizSnapshot.exists()) {
                        const quizData = quizSnapshot.val();
                        setQuiz(quizData);
                        setQuestions(quizData.questions || []);
                    }
                }

                setLoading(false);
            } catch (error) {
                console.error("Erreur lors de la récupération des résultats:", error);
                setError("Une erreur s'est produite lors de la récupération des résultats.");
                setLoading(false);
            }
        }

        fetchResults();
    }, [currentUser, quizId, sessionId, isTrainingMode]);

    // Préparer les données pour le graphique camembert
    const getPieChartData = () => {
        const correctAnswers = isTrainingMode
            ? results.results?.correctAnswers || 0
            : results.playerData?.questionsCorrect || 0;

        const totalQuestions = isTrainingMode
            ? results.playerData?.totalQuestions || questions.length
            : results.playerData?.totalQuestions || questions.length;

        const incorrectAnswers = totalQuestions - correctAnswers;

        return {
            labels: ['Correctes', 'Incorrectes'],
            datasets: [
                {
                    data: [correctAnswers, incorrectAnswers],
                    backgroundColor: ['rgba(75, 192, 192, 0.6)', 'rgba(255, 99, 132, 0.6)'],
                    borderColor: ['rgba(75, 192, 192, 1)', 'rgba(255, 99, 132, 1)'],
                    borderWidth: 1,
                },
            ],
        };
    };

    // Préparer les données pour le graphique de temps de réponse
    const getResponseTimeChartData = () => {
        if (!results || !results.responses) return null;

        const labels = [];
        const responseTimes = [];

        Object.entries(results.responses).forEach(([index, response]) => {
            const questionNumber = parseInt(index) + 1;
            labels.push(`Q${questionNumber}`);
            responseTimes.push(response.responseTime);
        });

        return {
            labels,
            datasets: [
                {
                    label: 'Temps de réponse (secondes)',
                    data: responseTimes,
                    backgroundColor: 'rgba(153, 102, 255, 0.6)',
                    borderColor: 'rgba(153, 102, 255, 1)',
                    borderWidth: 1,
                },
            ],
        };
    };

    // Options pour le graphique de temps de réponse
    const responseTimeChartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'Temps de réponse par question',
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'Secondes'
                }
            }
        }
    };

    // Calculer le score en pourcentage
    const getScorePercentage = () => {
        if (!results) return 0;

        const score = isTrainingMode
            ? results.results?.score || 0
            : results.playerData?.score || 0;

        const maxScore = questions.reduce((total, q) => total + (q.points || 1), 0);

        return maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
    };

    // Obtenir le niveau de performance
    const getPerformanceLevel = (percentage) => {
        if (percentage >= 90) return {text: 'Expert biblique !', class: 'text-green-600'};
        if (percentage >= 75) return {text: 'Excellent !', class: 'text-green-500'};
        if (percentage >= 60) return {text: 'Très bien !', class: 'text-blue-500'};
        if (percentage >= 40) return {text: 'Bon travail !', class: 'text-blue-400'};
        if (percentage >= 20) return {text: 'Continue à étudier !', class: 'text-yellow-500'};
        return {text: 'Il faut réviser !', class: 'text-red-500'};
    };

    // Formatter la date en français
    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';

        const date = new Date(timestamp);

        return new Intl.DateTimeFormat('fr-FR', {
            dateStyle: 'medium',
            timeStyle: 'short'
        }).format(date);
    };

    // Calculer la durée totale du quiz
    const getQuizDuration = () => {
        if (!results || !results.startedAt || !results.endedAt) return 'N/A';

        const startTime = new Date(results.startedAt);
        const endTime = new Date(results.endedAt);
        const durationMs = endTime - startTime;

        // Convertir en minutes et secondes
        const minutes = Math.floor(durationMs / 60000);
        const seconds = Math.floor((durationMs % 60000) / 1000);

        return `${minutes} min ${seconds} s`;
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-indigo-100">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-indigo-100">
                <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
                    <div className="text-center">
                        <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24"
                             stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                        </svg>
                        <h3 className="mt-2 text-lg font-medium text-gray-900">Erreur</h3>
                        <p className="mt-1 text-sm text-gray-500">{error}</p>
                        <div className="mt-6">
                            <button
                                onClick={() => navigate(-1)}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Retour
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!results) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-indigo-100">
                <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
                    <div className="text-center">
                        <svg className="mx-auto h-12 w-12 text-yellow-500" fill="none" viewBox="0 0 24 24"
                             stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                        </svg>
                        <h3 className="mt-2 text-lg font-medium text-gray-900">Résultats non disponibles</h3>
                        <div className="mt-6">
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Retourner au tableau de bord
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const scorePercentage = getScorePercentage();
    const performance = getPerformanceLevel(scorePercentage);

    return (
        <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-purple-100 pb-12">
            <Header title="Résultats du Quiz" showBackButton={true}/>
            <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                {/* Carte de résumé */}
                <div className="bg-white shadow-lg rounded-lg overflow-hidden mb-8">
                    <div className="px-6 py-5 border-b border-gray-200 bg-indigo-50">
                        <h2 className="text-lg font-medium text-indigo-900">
                            {isTrainingMode ? 'Session d\'entraînement terminée' : `Quiz : ${quiz?.metadata?.title || 'N/A'}`}
                        </h2>
                    </div>

                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <div className="flex flex-col items-center">
                                    <div className="relative">
                                        <svg className="w-32 h-32">
                                            <circle
                                                className="text-gray-200"
                                                strokeWidth="10"
                                                stroke="currentColor"
                                                fill="transparent"
                                                r="56"
                                                cx="64"
                                                cy="64"
                                            />
                                            <circle
                                                className={`${scorePercentage >= 75 ? 'text-green-500' : scorePercentage >= 50 ? 'text-yellow-500' : 'text-red-500'}`}
                                                strokeWidth="10"
                                                strokeDasharray={`${(scorePercentage / 100) * 2 * Math.PI * 56} ${2 * Math.PI * 56}`}
                                                strokeLinecap="round"
                                                stroke="currentColor"
                                                fill="transparent"
                                                r="56"
                                                cx="64"
                                                cy="64"
                                                transform="rotate(-90 64 64)"
                                            />
                                        </svg>
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <span className="text-3xl font-bold">{scorePercentage}%</span>
                                        </div>
                                    </div>
                                    <p className={`mt-2 text-xl font-semibold ${performance.class}`}>{performance.text}</p>
                                </div>

                                <div className="mt-6 grid grid-cols-2 gap-6">
                                    <div className="bg-indigo-50 p-4 rounded-lg">
                                        <p className="text-sm text-indigo-700 font-medium">Score</p>
                                        <p className="text-xl font-bold">
                                            {isTrainingMode
                                                ? `${results.results?.score || 0} points`
                                                : `${results.playerData?.score || 0} points`
                                            }
                                        </p>
                                    </div>
                                    <div className="bg-indigo-50 p-4 rounded-lg">
                                        <p className="text-sm text-indigo-700 font-medium">Réponses correctes</p>
                                        <p className="text-xl font-bold">
                                            {isTrainingMode
                                                ? `${results.results?.correctAnswers || 0}/${results.playerData?.totalQuestions || 0}`
                                                : `${results.playerData?.questionsCorrect || 0}/${results.playerData?.totalQuestions || 0}`
                                            }
                                        </p>
                                    </div>
                                    <div className="bg-indigo-50 p-4 rounded-lg">
                                        <p className="text-sm text-indigo-700 font-medium">Durée</p>
                                        <p className="text-xl font-bold">{getQuizDuration()}</p>
                                    </div>
                                    <div className="bg-indigo-50 p-4 rounded-lg">
                                        <p className="text-sm text-indigo-700 font-medium">Terminé le</p>
                                        <p className="text-xl font-bold">{formatDate(results.endedAt)}</p>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Répartition des réponses</h3>
                                <div className="h-64">
                                    <Pie data={getPieChartData()}/>
                                </div>
                            </div>
                        </div>

                        {/* Graphique de temps de réponse */}
                        {results.responses && Object.keys(results.responses).length > 0 && (
                            <div className="mt-8">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Temps de réponse par
                                    question</h3>
                                <div className="h-80">
                                    <Bar data={getResponseTimeChartData()} options={responseTimeChartOptions}/>
                                </div>
                            </div>
                        )}

                        {/* Verset d'encouragement */}
                        <div className="mt-8 p-6 bg-indigo-50 rounded-lg">
                            <blockquote className="italic text-indigo-800 text-center">
                                "Étudie et efforce-toi de te présenter devant Dieu comme un homme approuvé, un ouvrier
                                qui n'a pas à rougir et qui dispense avec droiture la parole de la vérité."
                                <footer className="mt-2 font-semibold">2 Timothée 2:15</footer>
                            </blockquote>
                        </div>

                        {/* Actions */}
                        <div className="mt-8 flex justify-between">
                            <Link
                                to="/dashboard"
                                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Retour au tableau de bord
                            </Link>

                            {isTrainingMode ? (
                                <Link
                                    to="/training"
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                    Nouvelle session d'entraînement
                                </Link>
                            ) : (
                                <Link
                                    to={`/quiz/${quizId}`}
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                    Rejouer ce quiz
                                </Link>
                            )}
                        </div>
                    </div>
                </div>

                {/* Détail des questions */}
                <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                    <div className="px-6 py-5 border-b border-gray-200 bg-indigo-50">
                        <h2 className="text-lg font-medium text-indigo-900">
                            Détail des questions
                        </h2>
                    </div>

                    <div className="divide-y divide-gray-200">
                        {Object.entries(results.responses || {}).map(([index, response]) => {
                            const questionIndex = parseInt(index);
                            const question = questions[questionIndex];

                            if (!question) return null;

                            return (
                                <div key={index} className="p-6">
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                                        Question {questionIndex + 1}: {question.text}
                                    </h3>

                                    <div className="space-y-3 mt-4">
                                        {question.options.map((option, optIndex) => (
                                            <div
                                                key={optIndex}
                                                className={`p-3 rounded-md ${
                                                    optIndex === question.correctAnswer
                                                        ? 'bg-green-100 border border-green-300'
                                                        : response.selectedOption === optIndex
                                                            ? 'bg-red-100 border border-red-300'
                                                            : 'bg-gray-50 border border-gray-200'
                                                }`}
                                            >
                                                <div className="flex items-start">
                                                    <div
                                                        className={`flex-shrink-0 h-5 w-5 rounded-full flex items-center justify-center mr-3 ${
                                                            optIndex === question.correctAnswer
                                                                ? 'bg-green-500 text-white'
                                                                : response.selectedOption === optIndex
                                                                    ? 'bg-red-500 text-white'
                                                                    : 'bg-gray-300 text-white'
                                                        }`}>
                                                        {['A', 'B', 'C', 'D'][optIndex]}
                                                    </div>
                                                    <p className={`${
                                                        optIndex === question.correctAnswer
                                                            ? 'text-green-800'
                                                            : response.selectedOption === optIndex
                                                                ? 'text-red-800'
                                                                : 'text-gray-800'
                                                    }`}>
                                                        {option}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="mt-4 bg-indigo-50 p-4 rounded-md">
                                        <p className="text-sm text-indigo-700">
                                            <span className="font-medium">Référence :</span> {question.verse}
                                        </p>
                                        {question.explanation && (
                                            <p className="text-sm text-indigo-700 mt-2">
                                                <span
                                                    className="font-medium">Explication :</span> {question.explanation}
                                            </p>
                                        )}
                                    </div>

                                    <div className="mt-3 text-sm text-gray-500">
                                        <p>
                                            <span
                                                className="font-medium">Temps de réponse :</span> {response.responseTime.toFixed(1)} secondes
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </main>
        </div>
    );
}
